import { MAX_PHASE_DAYS } from "@/constants/eventPhases";
import i18n from "@/i18n/i18n";

/**
 * Business refusals the customer is meant to read.
 *
 * The API writes `message` in English: the right choice for logs and for any
 * other consumer, but a red toast in a French interface saying "This
 * organisation already has a running subscription" is the product speaking the
 * wrong language at the worst moment. So the refusals a customer actually meets
 * carry a stable `code` in `details`, and the wording lives here in i18n.
 *
 * Only codes listed in the translations are honoured; anything else falls back
 * to the message the API sent, which is still better than nothing.
 */
const ERROR_CODE_PREFIX = "errors.codes";

/**
 * Values a coded wording may interpolate. The limits are the constants the
 * API enforces, never numbers copied into the translations.
 */
const ERROR_CODE_VALUES = { max: MAX_PHASE_DAYS };

interface ErrorPayload {
  details?: unknown;
}

/** The `code` the API attached to a refusal, when it attached one. */
const readCode = (payload: ErrorPayload | null | undefined): string | null => {
  if (!Array.isArray(payload?.details) || payload.details.length === 0) {
    return null;
  }

  const code = (payload.details[0] as { code?: unknown }).code;

  return typeof code === "string" && code.length > 0 ? code : null;
};

/**
 * The translated wording for an API refusal, or null when there is none:
 * which is what tells the caller to fall back to the API's own message.
 */
export const translateErrorCode = (
  payload: ErrorPayload | null | undefined,
): string | null => {
  const code = readCode(payload);

  if (!code) {
    return null;
  }

  // Keys are lower snake case; a code sent as a constant name
  // (`EVENT_DAY_WINDOWS_OVERLAP`) resolves to the same wording.
  const key = `${ERROR_CODE_PREFIX}.${code.toLowerCase()}`;

  // `i18next` echoes the key back when nothing matches, which would put
  // "errors.codes.something" in front of a customer.
  return i18n.exists(key) ? i18n.t(key, ERROR_CODE_VALUES) : null;
};
